// src/pages/MyAppointments.jsx (Upgraded and Final)

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import axiosInstance from "../context/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import moment from "moment";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Loader2,
  MailX,
  Send,
  Settings,
  UserCog,
  Building,
  CheckCircle,
  PartyPopper,
  ArrowUpRight,
} from "lucide-react";
import AppFooter from "./Homepage/AppFooter";

// --- Reusable UI Sub-Components ---

const DetailItem = ({ icon, children }) => (
  <div className="flex items-center gap-3 text-sm text-gray-400">
    {icon} <span>{children}</span>
  </div>
);

const RsvpQuestionnaire = ({
  questions,
  answers,
  onAnswerChange,
  disabled,
  entityId,
}) => {
  if (!questions || questions.length === 0) return null;

  return (
    <div className="space-y-4 mt-6 pt-6 border-t border-white/10">
      {questions.map((q) => {
        const isAttending = answers.q_attending;
        if (isAttending === false && q.questionId !== "q_attending")
          return null;

        const value = answers[q.questionId];

        return (
          <div key={q.questionId}>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {q.questionText}
            </label>
            {q.type === "boolean" ? (
              <select
                required={q.required}
                value={value === undefined ? "" : value ? "true" : "false"}
                onChange={(e) =>
                  onAnswerChange(
                    entityId,
                    q.questionId,
                    e.target.value === "true"
                  )
                }
                disabled={disabled}
                className="w-full h-11 bg-white/10 border border-white/20 rounded-lg px-3 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="" disabled>
                  Select...
                </option>
                <option className="text-black" value="true">
                  Yes
                </option>
                <option className="text-black" value="false">
                  No
                </option>
              </select>
            ) : (
              <input
                type={q.type}
                required={q.required}
                value={value || ""}
                onChange={(e) =>
                  onAnswerChange(entityId, q.questionId, e.target.value)
                }
                disabled={disabled}
                className="w-full h-11 bg-white/10 border border-white/20 rounded-lg px-3 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const RolePill = ({ roles }) => {
  // ✅ New component to display the user's primary role for the event
  let text = "";
  let style = "bg-gray-500/20 text-gray-300";

  if (roles.includes("host")) {
    text = "HOST";
    style = "bg-amber-500/20 text-amber-300";
  } else if (roles.includes("co-host")) {
    text = "CO-HOST";
    style = "bg-purple-500/20 text-purple-300";
  } else {
    return null; // Don't show a pill for regular invitees
  }

  return (
    <span
      className={`text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full ${style}`}
    >
      {text}
    </span>
  );
};

// --- Main Page Component ---

const MyAppointments = () => {
  const navigate = useNavigate();
  const { userID } = useAppContext();

  // ✅ State now matches the Flutter app's structure
  const [hostedEvents, setHostedEvents] = useState([]);
  const [coHostedEvents, setCoHostedEvents] = useState([]);
  const [acceptedEvents, setAcceptedEvents] = useState([]); // <-- NEW
  const [pendingInvites, setPendingInvites] = useState([]);
  const [memberInvitedOrgs, setMemberInvitedOrgs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [submittingMap, setSubmittingMap] = useState({});
  const [rsvpAnswers, setRsvpAnswers] = useState({});

  const isInviteValid = (expiryDateStr) => {
    if (!expiryDateStr) return true;
    return new Date() <= new Date(expiryDateStr);
  };

  // ✅ Updated fetch logic to categorize events correctly
  const fetchAllData = useCallback(async () => {
    if (!userID) return;
    setLoading(true);
    try {
      const [eventResponse, memberResponse] = await Promise.all([
        axiosInstance.get("/events/participating"),
        axiosInstance.get("/events/membership-invitations"),
      ]);

      const allEvents = eventResponse.data.events || [];
      const orgInvites = memberResponse.data.invites || [];

      const newHosted = [],
        newCoHosted = [],
        newAccepted = [],
        newPending = [];
      const now = new Date();

      allEvents.forEach((e) => {
        const roles = e.user_roles || [];
        const isCompleted = new Date(e.eventDateTime) < now;

        if (roles.includes("host")) {
          newHosted.push(e);
        } else if (roles.includes("co-host")) {
          newCoHosted.push(e);
        } else if (roles.includes("invitee") && !isCompleted) {
          // ✅ THE FIX: Find the current user's specific invitee record
          const myInviteeRecord = (e.inviteeRecords || []).find(
            (inv) => inv.userID === userID
          );

          // Now check the response from THAT record
          const isAttending =
            myInviteeRecord?.rsvpResponses?.q_attending === true;

          if (e.response_status === "responded" && isAttending) {
            // If they have responded AND said yes, it's an accepted event.
            newAccepted.push(e);
          } else if (["pending", "invited"].includes(e.response_status)) {
            // If their status is still pending, it's a pending invite.
            newPending.push(e);
          }
        }
      });

      // Sort all lists by date
      const sortByDate = (a, b) =>
        new Date(a.eventDateTime) - new Date(b.eventDateTime);
      newHosted.sort(sortByDate);
      newCoHosted.sort(sortByDate);
      newAccepted.sort(sortByDate);
      newPending.sort(sortByDate);

      const pendingMemberInvites = orgInvites.filter((i) =>
        ["pending", "invited"].includes(i.response_status)
      );

      setHostedEvents(newHosted);
      setCoHostedEvents(newCoHosted);
      setAcceptedEvents(newAccepted);
      setPendingInvites(newPending);
      setMemberInvitedOrgs(pendingMemberInvites);

 const initialAnswers = {};
 [...allEvents, ...orgInvites].forEach((invite) => {
   const entityId = invite.event_id || invite.org_id;
   if (entityId) {
     // Find the invitee record for the current user within this event/org invite
     const myInviteeRecord = (invite.inviteeRecords || []).find(
       (inv) => inv.userID === userID
     );

     // Use the responses from THAT specific record, or an empty object if not found
     initialAnswers[entityId] = myInviteeRecord?.rsvpResponses || {};
   }
 });
 setRsvpAnswers(initialAnswers);
    } catch (err) {
      console.error("Fetch Data Error:", err);
      toast.error("Failed to load your appointments.");
    } finally {
      setLoading(false);
    }
  }, [userID]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleAnswerChange = (entityId, questionId, value) => {
    setRsvpAnswers((prev) => ({
      ...prev,
      [entityId]: { ...prev[entityId], [questionId]: value },
    }));
  };

  const handleSubmit = async (entityId, type) => {
    const isEvent = type === "event";
    const endpoint = isEvent
      ? "/events/invitations/respond"
      : "/events/membership-invitations/respond";
    const payload = {
      user_id: userID,
      [isEvent ? "event_id" : "org_id"]: entityId,
      rsvpResponses: rsvpAnswers[entityId] || {},
    };

    if (payload.rsvpResponses.q_attending === undefined) {
      return toast.error("Please answer the 'attending' question first.");
    }

    setSubmittingMap((prev) => ({ ...prev, [entityId]: true }));

    toast
      .promise(axiosInstance.post(endpoint, payload), {
        loading: "Submitting your response...",
        success: () => {
          fetchAllData(); // Refresh list on success
          return "Your response has been saved!";
        },
        error: "Submission failed. Please try again.",
      })
      .finally(() => {
        setSubmittingMap((prev) => ({ ...prev, [entityId]: false }));
      });
  };

  // ✅ New function to render the actions for hosts and co-hosts
  const renderHostActions = (event) => (
    <div className="mt-4 pt-4 border-t border-white/10">
      <p className="text-sm text-gray-400 mb-4">
        As a host or co-host, you can manage event details and the guest list.
      </p>
      <button
        onClick={() =>
          navigate(`/app/events/${event.event_id}`, { state: { event } })
        }
        className="w-full bg-white/10 text-white font-bold py-2.5 rounded-full hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
      >
        <Settings size={16} />
        Manage Event
      </button>
    </div>
  );

  const renderRsvpForm = (invite, type) => {
    const isEvent = type === "event";
    const entityId = isEvent ? invite.event_id : invite.org_id;
    const isRsvpExpired = !isInviteValid(
      isEvent ? invite.inviteExpiryDate : invite.expiryDate
    );
    const isSubmitting = submittingMap[entityId];

    // Check if user has already responded 'Yes'
    const isAttending = rsvpAnswers[entityId]?.q_attending === true;

    if (isRsvpExpired && !isAttending) {
      return (
        <div className="text-center py-4 text-gray-500">
          This invitation has expired.
        </div>
      );
    }

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(entityId, type);
        }}
      >
        {isAttending && (
          <div className="p-3 mb-4 text-sm bg-green-500/20 text-green-300 rounded-lg flex items-center gap-2">
            <CheckCircle size={16} /> You have RSVP'd as attending. You can
            update your answers below.
          </div>
        )}
        <RsvpQuestionnaire
          questions={invite.rsvpQuestionnaire}
          answers={rsvpAnswers[entityId] || {}}
          onAnswerChange={handleAnswerChange}
          disabled={isSubmitting}
          entityId={entityId}
        />
        <button
          type="submit"
          disabled={isSubmitting}
          // ✅ The complete, correct className
          className="w-full mt-6 bg-white text-black font-bold py-2.5 rounded-full hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isSubmitting ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Send size={16} />
          )}
          {isSubmitting
            ? "Submitting..."
            : isAttending
            ? "Update Response"
            : "Submit Response"}
        </button>
      </form>
    );
  };

  // ✅ Main card rendering function, now with role-based logic
  const renderInviteCard = (invite, type) => {
    const isEvent = type === "event";
    const entityId = isEvent ? invite.event_id : invite.org_id;
    const title =
      invite.eventName ||
      invite.title ||
      (isEvent ? "Event Invite" : "Organization Invite");
    const roles = invite.user_roles || [];
    const isHostOrCoHost = roles.includes("host") || roles.includes("co-host");
    const isCompleted = isEvent
      ? new Date(invite.eventDateTime) < new Date()
      : false;

    // Also check the RSVP expiry for invitees
    const isRsvpExpired = !isInviteValid(
      isEvent ? invite.inviteExpiryDate : invite.expiryDate
    );
    return (
      <motion.div
        key={entityId}
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-surface/50 backdrop-blur-md border border-white/10 rounded-3xl p-8 hover:bg-surface/70 transition-all duration-300 shadow-xl"
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-lg font-bold text-white mb-1 group-hover:text-primary-300 transition-colors uppercase tracking-tight">{title}</h3>
             {invite.hostName && (
              <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
                {isEvent ? <UserCog size={14} className="text-primary-400" /> : <Building size={14} className="text-blue-400" />}
                <span>{isEvent ? `Hosted by ${invite.hostName}` : invite.hostName}</span>
              </div>
            )}
          </div>
          <RolePill roles={roles} />
        </div>

        {isEvent && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mb-6 bg-black/20 p-4 rounded-xl border border-white/5">
            <DetailItem icon={<Calendar size={16} className="text-secondary-400" />}>
              <span className="text-gray-200">{moment(invite.eventDateTime).format("MMM D, YYYY")}</span>
            </DetailItem>
            <DetailItem icon={<Clock size={16} className="text-secondary-400" />}>
              <span className="text-gray-200">{moment(invite.eventDateTime).format("h:mm A")}</span>
            </DetailItem>
            {invite?.venue && (
              <DetailItem icon={<MapPin size={16} className="text-secondary-400" />}>
                {typeof invite.venue === "string" ? (
                  <span className="text-gray-200">{invite.venue}</span>
                ) : (
                  <a
                    href={`https://www.google.com/maps?q=${invite.venue.lat},${invite.venue.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 group"
                  >
                    <span className="text-gray-200 group-hover:text-white transition decoration-dotted underline underline-offset-4">
                      {invite.venue.address}
                    </span>
                    <ArrowUpRight size={14} className="text-primary-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </a>
                )}
              </DetailItem>
            )}
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-white/10">
          {isCompleted ? (
            // If the event is over, show a "Completed" message for everyone.
            <div className="text-center py-2 text-green-400 flex items-center justify-center gap-2 bg-green-500/10 rounded-xl border border-green-500/20">
              <CheckCircle size={18} />
              <span className="font-medium">Event Completed</span>
            </div>
          ) : isHostOrCoHost ? (
            // If not completed AND user is a host/co-host, show manage button.
            renderHostActions(invite)
          ) : (
            // If not completed AND user is an invitee, show the RSVP form.
            renderRsvpForm(invite, type, isRsvpExpired)
          )}
        </div>
      </motion.div>
    );
  };

  const renderSection = (title, items, type, icon) => {
    if (items.length === 0) return null;
    return (
      <section>
        <h2 className="text-xl font-bold border-b border-white/10 pb-2 mb-6 flex items-center gap-2">
          {icon} {title}
        </h2>
        <div className="space-y-6">
          <AnimatePresence>
            {items.map((item) => renderInviteCard(item, type))}
          </AnimatePresence>
        </div>
      </section>
    );
  };

  // --- Main JSX ---
  return (
    <div className="bg-background text-white min-h-screen font-sans selection:bg-white selection:text-black">
      <main className="flex-grow">
        <div className="container mx-auto px-6 py-24 md:py-32">
          {/* Background Gradient */}
          <div className="fixed inset-0 pointer-events-none z-[-1]">
             <div className="absolute top-[10%] left-[20%] w-[500px] h-[500px] bg-primary-500/10 rounded-full blur-[120px]" />
             <div className="absolute bottom-[10%] right-[20%] w-[500px] h-[500px] bg-secondary-500/10 rounded-full blur-[120px]" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              onClick={() => navigate("/app")}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-widest mb-8"
            >
              <ArrowLeft size={16} /> Back to Dashboard
            </button>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tighter mb-4 text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-500">
              My Invitations
            </h1>
            <p className="text-base text-gray-400 max-w-2xl font-light">
              Review and respond to your pending event and organization invites.
            </p>
          </motion.div>
          <main className="mt-16 relative z-10">
            <AnimatePresence>
              {loading ? (
                <div className="flex flex-col items-center justify-center min-h-[30vh] text-gray-400">
                  <Loader2 size={32} className="animate-spin mb-4 text-primary-500" />
                  <p>Loading Your Appointments...</p>
                </div>
              ) : hostedEvents.length === 0 &&
                coHostedEvents.length === 0 &&
                acceptedEvents.length === 0 &&
                pendingInvites.length === 0 &&
                memberInvitedOrgs.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[30vh] text-center text-gray-400 border border-white/10 rounded-3xl p-12 bg-surface/30 backdrop-blur-sm">
                  <MailX size={48} className="mb-6 opacity-50" />
                  <h2 className="text-2xl font-bold text-white mb-2">
                    All Caught Up!
                  </h2>
                  <p className="max-w-md">You have no pending invitations or upcoming events. Why not create one?</p>
                </div>
              ) : (
                <div className="space-y-16">
                  {renderSection(
                    "My Hosted Events",
                    hostedEvents,
                    "event",
                    <UserCog className="text-primary-400" />
                  )}
                  {renderSection(
                    "Co-hosted Events",
                    coHostedEvents,
                    "event",
                    <Settings className="text-purple-400" />
                  )}
                  {renderSection(
                    "My Upcoming Events",
                    acceptedEvents,
                    "event",
                    <PartyPopper className="text-green-400" />
                  )}
                  {renderSection(
                    "Pending Invitations",
                    pendingInvites,
                    "event",
                    <MailX className="text-red-400" />
                  )}
                  {renderSection(
                    "Organization Invitations",
                    memberInvitedOrgs,
                    "org",
                    <Building className="text-blue-400" />
                  )}
                </div>
              )}
            </AnimatePresence>
          </main>
        </div>
      </main>
      <div className="container mx-auto px-6">
        <AppFooter />
      </div>
    </div>
  );
};

export default MyAppointments;
